import React, { useMemo } from 'react';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { get, upperFirst } from 'lodash';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useChargeQueries from '@dashboard/hooks/queries/useChargeQueries';
import useUrlState from '@dashboard/hooks/useUrlState';
import useTableSort from '@dashboard/hooks/useTableSort';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import ExportView from '@dashboard/components/ExportView';
import PageFilterInput from '@dashboard/components/Form/components/PageFilterInput';
import PageHeader from '@dashboard/components/PageHeader';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import Dash from '@dashboard/components/Dash';
import PaymentStatusBadge from '@dashboard/components/PaymentStatusBadge';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { formatDateTime } from '@dashboard/lib/format-date';
import isRecoveredAbandonment from '@dashboard/services/abandonment/isRecoveredAbandonment';
import isAbandonedAbandonment from '@dashboard/services/abandonment/isAbandonedAbandonment';
import { FILTER_OPTIONS } from '@dashboard/lib/chargesConstants';
import { getStatusFromQuery } from '@dashboard/lib/tableHelpers';
import { orderIdColumn } from '@dashboard/lib/orderId';
import PageTitle from '@dashboard/components/PageTitle';

/** Shared column definitions for payment tables */
export function getPaymentColumns({ options = {} } = {}) {
  const columns = [orderIdColumn];

  if (!options.hideCustomer) {
    columns.push({
      accessorKey: 'customerEmail',
      header: ({ column }) => <DataGridColumnHeader column={column} title="Customer" />,
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <div className="max-w-xs">
            <div className="font-medium">
              {(payment.customer && get(payment, 'customer.email')) ||
                get(payment, 'customerEmail') || <Dash />}
            </div>
            <div className="text-sm text-gray-500 dark:text-muted-foreground">
              {(payment.customer && get(payment, 'customer.name')) || '\u00A0'}
            </div>
          </div>
        );
      },
      enableSorting: true,
    });
  }

  columns.push({
    accessorKey: 'amount',
    header: ({ column }) => <DataGridColumnHeader column={column} title="Amount" />,
    cell: ({ row }) => {
      const payment = row.original;
      return (
        <span className="font-medium">
          {getPriceLabel({ amount: payment.amount, currency: payment.currency })}
        </span>
      );
    },
    enableSorting: true,
  });

  if (!options.hidePaymentLink) {
    columns.push({
      id: 'page',
      header: 'Product',
      cell: ({ row }) => (row.original.page && <PageTitle page={row.original.page} />) || <Dash />,
      enableSorting: false,
    });
  }

  columns.push({
    accessorKey: 'createdAt',
    header: ({ column }) => <DataGridColumnHeader column={column} title="Date" />,
    cell: ({ row }) => formatDateTime(row.original.createdAt),
    enableSorting: true,
  });

  if (!options.hideAbandonment) {
    columns.push({
      id: 'abandonment',
      header: 'Abandonment',
      cell: ({ row }) => {
        const payment = row.original;
        const isRecovered = isRecoveredAbandonment(payment);
        const isAbandoned = isAbandonedAbandonment(payment);

        if (isAbandoned) {
          return <PaymentStatusBadge status="abandoned" livemode={payment.livemode} />;
        }
        if (isRecovered) {
          return (
            <Tooltip label={`Recovered on ${formatDateTime(payment.recoveredAt)}`}>
              <StatusBadge status="recovered" label="Recovered" withInfoIcon />
            </Tooltip>
          );
        }
        return <Dash />;
      },
      enableSorting: false,
    });
  }

  if (!options.hideStatus) {
    columns.push({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const payment = row.original;
        const isRefunded = payment.status === 'refunded' || payment.status === 'partial_refund';

        if (payment.orderStatus === 'canceled' && isRefunded) {
          return (
            <div className="flex gap-1">
              <PaymentStatusBadge status={payment.orderStatus} livemode={payment.livemode} />
              <PaymentStatusBadge
                status={payment.status}
                livemode={payment.livemode}
                error={payment.paymentError}
              />
            </div>
          );
        }
        if (payment.orderStatus === 'canceled' && !isRefunded) {
          return <PaymentStatusBadge status={payment.orderStatus} livemode={payment.livemode} />;
        }
        return (
          <PaymentStatusBadge
            status={payment.status}
            livemode={payment.livemode}
            error={payment.paymentError}
          />
        );
      },
      enableSorting: false,
    });
  }

  return columns;
}

const PaymentsCharges = ({
  title = 'Payments',
  baseFilters = {},
  exportLabel = 'payments',
  hidePageFilter = false,
  embedded = false,
  filterOptions = FILTER_OPTIONS,
  hasCustomStatusFilter = false,
}) => {
  const [query, setParams] = useUrlState();
  const { sorting, onSortingChange, sort } = useTableSort();

  const search = query.search || '';
  const status = getStatusFromQuery(query.status, FILTER_OPTIONS);
  const page = query.page || undefined;

  const filter = {
    ...baseFilters,
    ...(FILTER_OPTIONS[status].value !== 'all' && {
      status: FILTER_OPTIONS[status].value,
    }),
    pageType: 'checkout',
    livemode: true,
    page: page || undefined,
  };

  const handleSearchChange = value => setParams({ search: value || undefined });

  const handleStatusChange = key =>
    setParams({
      status: FILTER_OPTIONS[key].value === 'all' ? undefined : FILTER_OPTIONS[key].value,
    });

  const handlePageChange = value => setParams({ page: value || undefined });

  const leftFilterControls = !hidePageFilter ? (
    <div className="w-full md:w-64">
      <PageFilterInput
        name="page"
        pageTypes={['checkout']}
        productTypes={['charge']}
        onChange={handlePageChange}
        initialValue={query.page}
        placeholder="Filter by checkout"
      />
    </div>
  ) : null;

  const filterControls = (
    <div className="flex flex-wrap items-center gap-2 max-md:w-full [&>*:first-child]:min-w-0 [&>*:first-child]:flex-1 lg:[&>*:first-child]:flex-none max-sm:[&>*:first-child]:basis-full">
      <SearchInput
        onChange={handleSearchChange}
        name="search_payments"
        placeholder="Search by email or order ID"
        initialValue={search}
        className="lg:w-80"
      />
      <FilterInput options={filterOptions} onChange={handleStatusChange} value={status} />
      <ExportView
        collection="payments"
        label={exportLabel}
        filters={{
          ...filter,
          sort: JSON.stringify(sort),
          search,
        }}
      />
    </div>
  );

  return (
    <div className={embedded ? 'space-y-4 px-4 pt-3 pb-4' : 'p-4 space-y-4'}>
      <PageHeader title={title} topBar={!embedded} />

      <PaymentsChargesList
        sort={sort}
        search={search}
        filter={filter}
        hasCustomStatusFilter={hasCustomStatusFilter}
        filterControls={filterControls}
        leftFilterControls={leftFilterControls}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
    </div>
  );
};

export function PaymentsChargesList({
  pageType,
  sort,
  search,
  filter = {},
  options = {},
  refreshKey,
  infinite = true,
  hasCustomStatusFilter = false,
  emptyStateTitle,
  emptyStateSubtitle,
  filterControls,
  leftFilterControls,
  sorting: sortingProp,
  onSortingChange,
}) {
  const { sellerId } = useAuth();
  const { getInfiniteCharges } = useChargeQueries();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading } =
    useInfiniteQuery({
      queryKey: ['charges', sellerId, sort, search, filter, refreshKey],
      placeholderData: keepPreviousData,
      queryFn: async ({ pageParam }) =>
        await getInfiniteCharges(sellerId, {
          ...filter,
          search,
          limit: 10,
          skip: pageParam * 10,
          sort: JSON.stringify(sort),
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!infinite) return undefined;
        if (!lastPage.has_more) return undefined;
        return allPages.length;
      },
    });

  const collection = pageType === 'event' ? 'bookings' : 'payments';

  // Flatten paginated data
  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // Column definitions
  // react-table mounts each `cell` function as a component: a new columns array remounts every cell.
  const { hideCustomer, hidePaymentLink, hideAbandonment, hideStatus } = options;
  const columns = useMemo(
    () =>
      getPaymentColumns({
        options: { hideCustomer, hidePaymentLink, hideAbandonment, hideStatus },
      }),
    [hideCustomer, hidePaymentLink, hideAbandonment, hideStatus],
  );

  // Empty state
  const isEmpty = !isLoading && flatData.length === 0;

  let emptyState;
  if (isEmpty && search) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>No {collection} found</EmptyState.Title>
        <EmptyState.Subtitle>There aren't any results for this filter.</EmptyState.Subtitle>
      </EmptyState>
    );
  } else if (isEmpty && emptyStateTitle) {
    emptyState = (
      <EmptyState>
        {emptyStateTitle && <EmptyState.Title>{emptyStateTitle}</EmptyState.Title>}
        {emptyStateSubtitle && <EmptyState.Subtitle>{emptyStateSubtitle}</EmptyState.Subtitle>}
      </EmptyState>
    );
  } else if (isEmpty && filter.status && !hasCustomStatusFilter) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>No {collection} yet</EmptyState.Title>
        <EmptyState.Subtitle>Your {collection} will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  } else if (isEmpty) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>No {collection} yet</EmptyState.Title>
        <EmptyState.Subtitle>Your {collection} will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  if (isError) {
    return (
      <div className="m-4">Unable to show {collection}. Please refresh or contact support.</div>
    );
  }

  return (
    <DataGrid
      columns={columns}
      data={flatData}
      isLoading={isLoading}
      paginationMode={infinite ? 'infinite' : undefined}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      sorting={sortingProp}
      onSortingChange={onSortingChange}
      toolbar={
        filterControls || leftFilterControls
          ? {
              leftActions: leftFilterControls,
              customActions: filterControls,
            }
          : undefined
      }
      getRowHref={payment => {
        const isBooking = payment.tickets && payment.tickets.length > 0;
        return isBooking ? `/bookings/${payment._id}` : `/payments/${payment._id}`;
      }}
      emptyState={emptyState}
      emptyMessage={`No ${collection} found`}
    />
  );
}

export default PaymentsCharges;
